import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { withPage } from '../../components/Page';
import { PrototypeBar } from '../../components/PrototypeBar';
import { QrLabelModal } from '../../components/QrLabelModal';
import { QrCodeSvg } from '../../components/QrCodeSvg';
import styles from './PlantRoom.module.scss';

const PlantRoom: FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <main className={styles.container} tabIndex={-1}>
      <PrototypeBar />

      <div className={styles.pageTitle}>
        <div>
          <div className={styles.eyebrow}>Locations / Building A / Level 01</div>
          <h1 className={styles.title}>Plant Room 01</h1>
          <p className={styles.subtitle}>Mechanical plant room · Building A · Level 01</p>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => setIsModalOpen(true)}
          >
            ▦ Room QR code
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => navigate('/contextual-ai')}
          >
            ✦ Ask AI about this room
          </button>
        </div>
      </div>

      <div className={styles.roomStats}>
        <div className={`${styles.card} ${styles.roomStat}`}>
          <span>Assets in this location</span>
          <b>18</b>
        </div>
        <div className={`${styles.card} ${styles.roomStat}`}>
          <span>Related documents</span>
          <b>6</b>
        </div>
        <div className={`${styles.card} ${styles.roomStat}`}>
          <span>Current attention items</span>
          <b style={{ color: 'var(--color-yellow)' }}>2</b>
        </div>
      </div>

      <div className={styles.grid2}>
        <div className={styles.mainCol}>
          <div className={`${styles.card} ${styles.section}`}>
            <h2 className={styles.sectionTitle}>Assets in Plant Room 01</h2>
            <p className={styles.sectionDesc}>
              Assets associated with this location. Scan a room QR to find the right equipment without navigating the information hierarchy.
            </p>
            <div className={styles.tableWrapper}>
              <table className={styles.assetTable}>
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Type</th>
                    <th>System</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className={styles.assetName} onClick={() => navigate('/asset-data/ahu-003')} style={{ cursor: 'pointer', color: 'var(--color-blue)' }}>
                        AHU-003
                      </span>
                    </td>
                    <td>Air Handling Unit</td>
                    <td>Ventilation</td>
                    <td>
                      <span className={styles.badge}>Operational</span>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <a href="/more-info" className={styles.assetName}>
                        CHWP-002
                      </a>
                    </td>
                    <td>Chilled Water Pump</td>
                    <td>CHW LTHW</td>
                    <td>
                      <span className={styles.badge}>Operational</span>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <a href="/more-info" className={styles.assetName}>
                        P-014
                      </a>
                    </td>
                    <td>Heating Pump</td>
                    <td>LTHW</td>
                    <td>
                      <span className={`${styles.badge} ${styles.due}`}>Service due</span>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <a href="/more-info" className={styles.assetName}>
                        DB-04
                      </a>
                    </td>
                    <td>Distribution Board</td>
                    <td>Electrical</td>
                    <td>
                      <span className={styles.badge}>Operational</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className={`${styles.card} ${styles.section}`}>
            <h2 className={styles.sectionTitle}>Room information</h2>
            <div className={styles.facts}>
              <div className={styles.fact}>
                <span className={styles.label}>Room reference</span>
                <b>BA-L01-PR01</b>
              </div>
              <div className={styles.fact}>
                <span className={styles.label}>Access</span>
                <b>Authorised personnel</b>
              </div>
              <div className={styles.fact}>
                <span className={styles.label}>Primary systems</span>
                <b>Mechanical / Electrical</b>
              </div>
              <div className={styles.fact}>
                <span className={styles.label}>Last reviewed</span>
                <b>14 Jul 2026</b>
              </div>
            </div>
          </div>

          <div className={`${styles.card} ${styles.section}`}>
            <h2 className={styles.sectionTitle}>Related information</h2>
            <div className={styles.docRow}>
              <div className={styles.file}>PDF</div>
              <div>
                <b>Plant Room 01 O&amp;M Index</b>
                <small>General information · 1.2 MB</small>
              </div>
              <a href="/more-info" className={styles.openLink}>
                Open ↗
              </a>
            </div>
            <div className={styles.docRow}>
              <div className={styles.file}>DWG</div>
              <div>
                <b>Mechanical services layout</b>
                <small>M-PL01-200 · Drawing</small>
              </div>
              <a href="/more-info" className={styles.openLink}>
                Open ↗
              </a>
            </div>
          </div>
        </div>

        <aside className={styles.asideCol}>
          <div className={`${styles.card} ${styles.section}`}>
            <h2 className={styles.sectionTitle}>Pulse Location QR</h2>
            <p className={styles.sectionDesc}>
              Placed at the room entrance, it opens this location and its associated records.
            </p>
            <div className={styles.roomQr}>
              <div className={styles.qr}>
                <QrCodeSvg />
              </div>
              <div>
                <b>Plant Room 01</b>
                <br />
                <span style={{ color: 'var(--color-text-secondary)', fontSize: '1.2rem' }}>
                  PUL-CBK-LOC-PL01
                </span>
                <br />
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  style={{ marginTop: '1rem', height: '3.4rem', fontSize: '1.2rem' }}
                  onClick={() => setIsModalOpen(true)}
                >
                  Preview label
                </button>
              </div>
            </div>
          </div>

          <div className={`${styles.card} ${styles.section} ${styles.maintenanceCallout}`}>
            <h3>Open items</h3>
            <p>
              P-014 planned service due 08 October. Fire damper access record expires this month.
            </p>
          </div>
        </aside>
      </div>

      <QrLabelModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} type="location" />
    </main>
  );
};

export default withPage(PlantRoom)({
  meta: {
    title: 'Plant Room 01 — Locations · Pulse',
    description: 'Plant Room 01 location record in Building A, Level 01.',
  },
  breadcrumbs: [
    { label: 'Pulse', href: '/' },
    { label: 'Commerzbank', href: '/' },
    { label: 'Locations', href: '/locations/plant-room-01' },
    { label: 'Plant Room 01', href: '/locations/plant-room-01' },
  ],
});
